import React, { useState, useEffect } from "react";
import { ReactSelect } from "@anchor/react-components/dist/lib/components";
import { FetchUserProfileService } from "../../services/fetchUserProfileService";
import { formatProfiles } from "../Collaborators/helpers/formatProfiles";

/**
 * Reusable component for selecting users via Microsoft Graph API
 * Similar to Collaborators dropdown but for single user selection
 * 
 * @param {string} value - Current email value
 * @param {function} onChange - Callback when user selects someone: (email, displayName) => void
 * @param {string} placeholder - Placeholder text for input
 * @param {boolean} disabled - Whether the input is disabled
 * @param {boolean} required - Whether the field is required
 * @param {string} label - Label for the input
 * @param {string} errorMessage - Error message to display
 */
function UserPickerSelect({
  value,
  onChange,
  placeholder = "Type name to search...",
  disabled = false,
  required = false,
  label = "",
  errorMessage = "",
}) {
  const [fetchingProfiles, setFetchingProfiles] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const { fetchUserProfile } = FetchUserProfileService();

  // Fetch display name when value (email) changes
  useEffect(() => {
    if (value && value.trim() !== "") {
      // If we have an email, try to get the display name
      fetchDisplayNameForEmail(value);
    } else {
      setDisplayName("");
      setSelectedOption(null);
    }
  }, [value]);

  const fetchDisplayNameForEmail = async (email) => {
    try {
      setFetchingProfiles(true);
      const profiles = await fetchUserProfile(email);
      const formattedProfiles = formatProfiles(profiles.value);
      
      // Find the exact match
      const match = formattedProfiles.find(
        (p) => p.value.toLowerCase() === email.toLowerCase()
      );
      
      if (match) {
        setDisplayName(match.label);
        setSelectedOption({ label: match.label, value: match.value });
      } else {
        // If no match found, just display the email
        setDisplayName(email);
        setSelectedOption({ label: email, value: email });
      }
    } catch (error) {
      console.error("Error fetching display name:", error);
      setDisplayName(email);
      setSelectedOption({ label: email, value: email });
    } finally {
      setFetchingProfiles(false);
    }
  };

  const getUserProfiles = async (searchText) => {
    if (searchText && searchText.trim().length > 0) {
      setFetchingProfiles(true);
      try {
        const profilesData = await fetchUserProfile(searchText);
        const formattedProfiles = formatProfiles(profilesData.value);
        setProfiles(formattedProfiles);
      } catch (error) {
        console.error("Error fetching user profiles:", error);
        setProfiles([]);
      } finally {
        setFetchingProfiles(false);
      }
    }
  };

  const handleSelectUser = (selectedItem) => {
    if (selectedItem) {
      const email = selectedItem.value;
      const name = selectedItem.label;
      
      setSelectedOption(selectedItem);
      setDisplayName(name);
      
      // Call parent onChange with both email and display name
      if (onChange) {
        onChange(email, name);
      }
    } else {
      // User cleared the selection
      setSelectedOption(null);
      setDisplayName("");
      if (onChange) {
        onChange("", "");
      }
    }
  };

  return (
    <div style={{ width: "100%" }}>
      <ReactSelect
        isSearchable
        className="react-select"
        closeMenuOnSelect
        customNoOptionsMessage={<p>No users found</p>}
        errorMessage={errorMessage}
        fit="medium"
        hint=""
        hintPosition="left"
        isClearable
        label={label}
        onChange={handleSelectUser}
        onInputChange={getUserProfiles}
        isLoading={fetchingProfiles}
        noOptionsMessage={() => "Type to search for users"}
        options={profiles && profiles.length > 0 ? profiles : []}
        orientation="vertical"
        placeholder={placeholder}
        required={required}
        suggestType="static"
        value={selectedOption}
        variant="default"
        width={100}
        defaultInputValue=""
        error={!!errorMessage}
        disabled={disabled}
      />
      
      {/* Display selected user's full name below the picker */}
      {displayName && value && (
        <div style={{ 
          fontSize: "11px", 
          color: "#555", 
          marginTop: "4px",
          padding: "3px 6px",
          backgroundColor: "#f5f5f5",
          borderRadius: "3px",
          border: "1px solid #e0e0e0",
          wordBreak: "break-word",
          lineHeight: "1.3"
        }}>
          <strong>Selected:</strong> {displayName}
        </div>
      )}
    </div>
  );
}

export default UserPickerSelect;
